/**
 * 急停面板 —— **云端此刻停不了任何东西,所以这里必须把话说死。**
 *
 * 原型的这个红按钮点下去只改了一个 React state:界面显示"已急停",而设备那边
 * 什么都没收到。这是整个控制台里最危险的一处假数据 —— 一个假的急停按钮,
 * 比没有急停按钮糟得多:人会因为看见它而不去做真正管用的那件事。
 *
 * 事实是:hub 只有设备身份、台账、入网、事件广播,**没有下行控制通道**
 * (design-cloud-hub-edge-autonomy.md:控制留在边缘)。所以按钮禁用,并写清楚
 * 现在真正能停狗的三条路。哪天 hub 有了下行通道,再把它接上并改掉这段注释。
 */
import { useState } from 'react';
import { useTerminals } from '@/hooks/useHubData';
import { timeAgo } from '@/lib/format';

const REAL_WAYS = [
  { icon: 'ri-remote-control-line', text: '设备本体的物理急停开关 —— 任何时候都最可靠' },
  { icon: 'ri-dashboard-3-line', text: '连到那台设备的座舱(经私有网直连),用它的停止/取消' },
  { icon: 'ri-terminal-line', text: 'ssh 上去按管家的规矩停对应组件' },
];

export default function EmergencyStop() {
  const [showPanel, setShowPanel] = useState(false);
  const { terminals, error } = useTerminals();

  return (
    <>
      <button
        onClick={() => setShowPanel(!showPanel)}
        className="fixed bottom-[max(1.5rem,env(safe-area-inset-bottom))] right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center transition-all cursor-pointer bg-background-700 hover:bg-background-600"
        title="急停 —— 云端暂无下行控制通道"
      >
        <i className="ri-shut-down-line text-white text-xl"></i>
      </button>

      {showPanel && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowPanel(false)}></div>
          <div className="fixed bottom-24 right-4 left-4 sm:left-auto sm:right-6 z-50 sm:w-80 max-h-[70vh] overflow-y-auto bg-background-50 border border-background-200/70 rounded-xl p-4 shadow-lg">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-foreground-900 font-heading">急停控制</h3>
              <button onClick={() => setShowPanel(false)} className="w-9 h-9 flex items-center justify-center rounded hover:bg-background-100 cursor-pointer">
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>

            <div className="rounded-lg bg-red-50 border border-red-200/70 p-3 mb-3">
              <p className="text-xs font-semibold text-red-700 mb-1">云端还不能下发急停</p>
              <p className="text-[11px] text-red-700/90 leading-relaxed">
                hub 只做设备身份、任务台账、入网和事件广播,**没有到设备的下行控制通道**。
                所以这里不提供一个按下去只会让界面变红的按钮。
              </p>
            </div>

            <button disabled className="w-full py-2.5 rounded-lg text-sm font-semibold mb-3 bg-background-200 text-foreground-400 cursor-not-allowed whitespace-nowrap">
              全局急停 · 通道未建
            </button>

            <p className="text-[11px] font-medium text-foreground-600 mb-1.5">现在真能停下设备的方式</p>
            <ul className="space-y-1.5 mb-3">
              {REAL_WAYS.map((w) => (
                <li key={w.text} className="flex items-start gap-2 text-[11px] text-foreground-600 leading-relaxed">
                  <i className={`${w.icon} text-sm text-foreground-400 mt-px shrink-0`} />{w.text}
                </li>
              ))}
            </ul>

            <div className="border-t border-background-200/70 pt-2">
              <p className="text-[11px] text-foreground-500 mb-1.5">已注册设备(仅供核对,不能在此操作)</p>
              {error ? (
                <p className="text-[11px] text-red-600">读设备列表失败:{error}</p>
              ) : terminals.length === 0 ? (
                <p className="text-[11px] text-foreground-400">注册表为空。</p>
              ) : (
                <ul className="space-y-1 max-h-40 overflow-y-auto">
                  {terminals.map((t) => (
                    <li key={t.id} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded text-[11px]">
                      <span className="flex items-center gap-1.5 min-w-0">
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${t.status === 'online' ? 'bg-accent-500' : 'bg-foreground-300'}`} />
                        <span className="truncate text-foreground-700">{t.name}</span>
                      </span>
                      <span className="text-foreground-400 shrink-0 font-mono">{timeAgo(t.lastSeen)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
